import React, {useCallback, useEffect, useRef, useState} from "react";
import ProfileHeader from "../Headers/ProfileHeader";
import FormFileInput from "react-bootstrap/FormFileInput";
import {FileDrop} from "react-file-drop";
import {getFiles, getSignedUrl, postFile, putFile, putFolder, uploadFile} from "../../lib/api";
import {FolderAddButton} from "../Shared/Buttons";
import FileList from "./FileList";
import FileEdit from "./FileEdit";
import {Button} from "react-bootstrap";
import * as Icon from 'react-bootstrap-icons';
import update from 'immutability-helper';

// TODO: filter only png and jpeg when drag & drop
// TODO: skip files > 10mb or¬ something
// TODO: fake progress indicators, and show uploading files n of m
// TODO: handle and display multiple errors, use Promise.all for uploads

const DEFAULT_FILE_MESSAGE = "Drag & drop files or click to upload";

function Breadcrumb(props: { folder: any, navigate: any }) {
  const {folder, navigate} = props;
  const name = folder.name === "/" ? "My Library" : folder.name;
  return (<li
    className="breadcrumb px-1 py-0"
  ><Button onClick={() => navigate(folder)}>{name}</Button></li>);
}

function Breadcrumbs(props: { breadcrumbs: any, navigate: any }) {
  const {breadcrumbs, navigate} = props;
  const rootFolder = breadcrumbs[0];
  if (rootFolder === undefined) {
    return null;
  }
  const breadcrumbComponents = [<Breadcrumb folder={rootFolder} navigate={navigate} key="rootFolder"/>];
  breadcrumbs.slice(1).forEach((folder: any, i: number) => {
    let key = `breadcrumb-spacer-${i}`;
    breadcrumbComponents.push(<li className="mx-0" key={key}><Icon.ChevronRight size={20}/></li>)
    key = `breadcrumb-` + folder.id;
    breadcrumbComponents.push(<Breadcrumb folder={folder} navigate={navigate} key={key}/>);
  });
  return (
    <ul className="breadcrumbs list-group list-group-horizontal px-0 mx-0 align-items-baseline d-flex flex-wrap">
      {breadcrumbComponents}
    </ul>
  );
}

function Library(props: { cognitoUser: any, loadData: () => Promise<void> }) {
  const {cognitoUser, loadData} = props;
  // const [filesUpload, setFilesUpload] = useState([]);
  const [files, setFiles] = useState([]);
  const [folder, setFolder] = useState({id: null, fileType: "", name: '', files: [], folderId: -1, seq: -1});
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [error, setError] = useState(null);
  const [showEditFile, setShowEditFile] = useState(false);
  const [editableFile, setEditableFile] = useState({id: null, name: '', description: '', files: [], folderId: -1});
  const [fileMessage] = useState(DEFAULT_FILE_MESSAGE);
  const [viewFile, setViewFile] = useState<null | any>(null);
  const allowedFileTypes = [".obj", ".fbx", ".gltf", ".glb", ".jpg", ".jpeg", ".png", ".zip",".mp3"]


  useEffect(() => {
      refreshFiles();
    }, []
  )

  function pushBreadcrumb(folder: any) {
    const newBreadcrumb = [...breadcrumbs, folder];
    // @ts-ignore
    setBreadcrumbs(newBreadcrumb);
  }

  function updateBreadcrumbs(json: any) {
    const newBreadcrumbs: any = [];
    breadcrumbs.forEach((breadcrumb: any) => {
      const foundFolder = findFolder(json, breadcrumb);
      newBreadcrumbs.push(foundFolder);
    });
    setBreadcrumbs(newBreadcrumbs);
  }

  function findFolder(json: any, folder: any) {
    if (folder.id === json.id) {
      return json;
    } else {
      let foundFolder: any = null;
      json.files.forEach((f: any) => {
        if (folder.id === f.id) {
          foundFolder = f;
        } else if (f.fileType === "folder" && foundFolder === null) {
          foundFolder = findFolder(f, folder);
        }
      });
      return foundFolder;
    }
  }

  function refreshFiles() {
    getFiles().then(response => {
      if (response.ok) {
        response.json().then(json => {
          if (breadcrumbs.length === 0) {
            setFolder(json);
            setFiles(json.files);
            pushBreadcrumb(json);
          } else {
            const foundFolder = findFolder(json, folder);
            if (foundFolder) {
              setFolder(foundFolder);
              setFiles(foundFolder.files);
            }
            updateBreadcrumbs(json);
          }
        });
      }
    })
  }

  function responseHandler(response: any) {
    if (response.ok) {
      refreshFiles();
    } else {
      response.json().then((json: any) => {
        console.log("error json", json);
        setError(json?.error);
      });
    }
  }

  function onHideEditFolder() {
    setShowEditFile(false);
    refreshFiles();
  }

  function addFolder() {
    setEditableFile({id: null, name: "", description: '', files: [], folderId: folder.folderId})
    setShowEditFile(true);
    return false;
  }

  function editFile(file: any) {
    setEditableFile(file)
    setShowEditFile(true);
  }

  const moveFile = useCallback(
    (id: string, dragIndex: number, hoverIndex: number, dropped: boolean) => {
      const dragFile: any = files[dragIndex]
      const hoverFile: any = files[hoverIndex]
      if (hoverFile.fileType !== "folder" && dragFile.fileType !== "folder") {
        setFiles(
          update(files, {
            // @ts-ignore
            $splice: [
              [dragIndex, 1],
              [hoverIndex, 0, dragFile],
            ],
          }),
        )
      }
      if (dropped) {
        setError(null);
        if (hoverFile.fileType === "folder") {
          // move file to this folder
          const file: any = files.find((f: any) => f.id === id);
          if (file.fileType === "folder") {
            putFolder(id, {folderId: hoverFile.id}).then(responseHandler);
          } else {
            // send seq: 0 to cause it to re-sequence the files in the new folder and put this file at the end
            putFile(id, {folderId: hoverFile.id, seq: 0}).then(responseHandler);
          }
        } else {
          const index = files.findIndex((file: any) => file.id === id);
          let seq = 0;
          if (index > 0) {
            const previousFile: any = files[index - 1];
            if (previousFile.fileType === "file") {
              seq = previousFile.seq + 1;
            }
          }
          putFile(id, {seq: seq}).then(responseHandler);
        }
      }
    },
    [files]
  );

  function handleFileInput(e: any) {
    // setFilesUpload(e.target.files);
    uploadFiles(e.target.files);
  }

  const fileInputRef = useRef<HTMLInputElement>(null)

  function onTargetClick() {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }

  function onDrop(args: any) {
    // setFilesUpload(args);
    uploadFiles(args);
  }

  function uploadProgress(progress: number, total: number) {
    console.log('progress', progress, 'total', total)
  }

  function checkAllowedFile(file: any): boolean {
    for (let ext in allowedFileTypes) {
      if (file.name.toLowerCase().endsWith(allowedFileTypes[ext])) {
        return true;
      }
    }

    return false;
  }

  function uploadSingleFile(file: any): void {
    getSignedUrl(file).then((response) => {
      response.json().then(json => {
        // setFileMessage(`Uploading file ${file.name} ${i+1} of ${files.length}`)
        uploadFile(json.postUrl, file, uploadProgress)
          .then(uploadResponse => {
            postFile(file, json.getUrl, folder.id).then(fileResponse => {
              if (fileResponse.ok) {
                refreshFiles();
              } else {
                fileResponse.json().then(json => {
                  console.log("error json", json);
                  setError(json?.error);
                });
              }
            })
          }).catch(error => {
          console.log('error uploading', error);
        })
      })
    });
  }

  function uploadFiles(files: any) {
    setError(null);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (checkAllowedFile(file)) {
        // TODO: Promise.all with a final block to set the file message back
        uploadSingleFile(file)
      }
    }
    // setFileMessage(DEFAULT_FILE_MESSAGE);
  }

  function navigate(file: any) {
    setError(null);
    if (file.fileType === "folder") {
      setViewFile(null);
      // check if it's in the current breadcrumb and if so set new breadcrumb, else push
      const index = breadcrumbs.findIndex((breadcrumb: any) => breadcrumb.id === file.id)
      if (index > -1) {
        setBreadcrumbs(breadcrumbs.slice(0, index + 1));
        setFolder(file);
        setFiles(file.files);
      } else {
        pushBreadcrumb(file);
        setFolder(file);
        setFiles(file.files);
      }
    } else {
      setViewFile(file);
    }
  }

  const errorMessage = error ?
    (
      <div className="row error">
        <div className="col">
          Error: {(error as any)?.message}
        </div>
      </div>
    )
    : null;

  const fileListAndDropAreaComponents = (
    <>
      <div className="row">
        <div className="col">
          <FileList files={files} navigate={navigate} editFile={editFile} moveFile={moveFile}/>
        </div>
      </div>
      <div className="row">
        <div className="col pt-4 text-center">
          <FormFileInput className="d-none"
                         ref={fileInputRef}
                         type="file"
                         onChange={handleFileInput}
                         accept={allowedFileTypes.join()}
                         multiple/>
          <FileDrop className="file-drop border" onTargetClick={onTargetClick} onDrop={onDrop}>
            {fileMessage}
          </FileDrop>
        </div>
      </div>
    </>);

  const viewFileComponent = (
    <>
      <div className="row">
        <div className="col text-center">
          <h3>{viewFile?.name}</h3>
        </div>
      </div>
      <div className="row">
        <div className="col px-4 text-center">
          <img alt={viewFile?.name} src={viewFile?.url} className="view-file"/>
        </div>
      </div>
    </>
  );

  const body = viewFile ? viewFileComponent : fileListAndDropAreaComponents;

  return (
    <>
      <ProfileHeader cognitoUser={cognitoUser} title="Bookmarks" linkToHome="/home"/>
      <section className="container main library p-4 rounded">
        <div className="row">
          <div className="col-3">&nbsp;</div>
          <div className="col-6 text-center instructions">
            You can upload png and jpeg images and share a single image or an entire folder
          </div>
          <div className="col-3">&nbsp;</div>
        </div>
        <div className="row">
          <div className="col-3">&nbsp;</div>
          <div className="col-6 pb-4 text-center instructions">
            Also upload and share 3D models including obj, gltf, glb and fbx
          </div>
          <div className="col-3">&nbsp;</div>
        </div>
        {errorMessage}
        {
          viewFile ? (
            <div className="row">
              <div className="col text-left">
                <Button onClick={() => setViewFile(null)}><Icon.ChevronLeft size={20}/>Back&nbsp;</Button>
              </div>
            </div>
          ) : (
            <div className="row">
              <div className="col-10 px-0">
                <Breadcrumbs breadcrumbs={breadcrumbs} navigate={navigate}/>
              </div>
              <div className="col-2 text-right">
                <a href="/#" onClick={(e) => {
                  addFolder();
                  e.preventDefault();
                }}>New Folder<FolderAddButton visible={true} onClick={addFolder}/></a>
              </div>
            </div>
          )
        }
        {body}
        <FileEdit file={editableFile}
                  parentFolderId={folder.id}
                  loadData={loadData}
                  show={showEditFile}
                  onHide={onHideEditFolder}
        />
      </section>
    </>
  );
}

export default Library;
